import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api, ACTIVITIES_PRIMARY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Sparkles, Loader2, Trash2, Users, Calendar as CalendarIcon, MapPin, RefreshCw, Clock, Check, Star, Wallet, CalendarCheck, Tag, BadgeCheck, Heart } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const CAT_IMG = {
  "Sports": "https://images.unsplash.com/photo-1772517403292-b68a77fbc342?auto=format&fit=crop&w=600&q=70",
  "Indoor Sports": "https://images.pexels.com/photos/36765297/pexels-photo-36765297.jpeg?auto=compress&cs=tinysrgb&w=600",
  "Billiard Games": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=600&q=70",
  "Board Games": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=600&q=70",
  "Video Games": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=600&q=70",
  "Dining": "https://images.pexels.com/photos/25409638/pexels-photo-25409638.jpeg?auto=compress&cs=tinysrgb&w=600",
  "Bars": "https://images.pexels.com/photos/25409638/pexels-photo-25409638.jpeg?auto=compress&cs=tinysrgb&w=600",
  "Clubs": "https://images.pexels.com/photos/36765297/pexels-photo-36765297.jpeg?auto=compress&cs=tinysrgb&w=600",
  "Beach": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=600&q=70",
  "Concerts": "https://images.pexels.com/photos/36765297/pexels-photo-36765297.jpeg?auto=compress&cs=tinysrgb&w=600",
  "Art & Crafts": "https://images.pexels.com/photos/25409638/pexels-photo-25409638.jpeg?auto=compress&cs=tinysrgb&w=600",
  "Other": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=600&q=70",
};

const CAT_EMOJI = {
  "Sports": "⚽",
  "Indoor Sports": "🏓",
  "Billiard Games": "🎱",
  "Board Games": "🎲",
  "Video Games": "🎮",
  "Dining": "🍽️",
  "Bars": "🍻",
  "Clubs": "💃",
  "Beach": "🏖️",
  "Concerts": "🎤",
  "Art & Crafts": "🎨",
};

export default function Events() {
  const { t } = useLang();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [scope, setScope] = useState("personal");
  const [category, setCategory] = useState(null); // null = All
  const [source, setSource] = useState("all"); // "all" | "curated" | "ai"
  const autoTried = useRef(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const [evs, gs] = await Promise.all([
        api.get("/events"),
        api.get("/groups"),
      ]);
      setEvents(evs.data);
      setGroups(gs.data);
      return evs.data;
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = events;
    if (scope === "personal") {
      list = list.filter((e) => e.scope === "personal");
    } else {
      list = list.filter((e) => e.scope === "group" && e.group_id === scope);
    }
    if (category) list = list.filter((e) => e.category === category);
    if (source === "curated") list = list.filter((e) => e.curated === true);
    else if (source === "ai") list = list.filter((e) => !e.curated);
    else if (source === "saved") list = list.filter((e) => e.saved === true);
    return list;
  }, [events, scope, category, source]);

  // Auto-generate when scope+category combo is empty and we haven't tried yet this session
  useEffect(() => {
    if (loading || generating) return;
    if (source === "saved") return; // Never auto-generate on the Saved shelf
    if (filtered.length > 0) return;
    const key = `${scope}::${category || "all"}`;
    if (autoTried.current.has(key)) return;
    autoTried.current.add(key);
    generate();
  }, [loading, scope, category, filtered.length, source]);

  const generate = async (overrideCategory) => {
    setGenerating(true);
    try {
      const cat = overrideCategory !== undefined ? overrideCategory : category;
      const body = scope === "personal" ? { scope: "personal" } : { scope: "group", group_id: scope };
      if (cat) body.category = cat;
      // The endpoint fires an async background job and returns in <500ms with
      // {status:'generating'}. We poll /events for ~60s to see the new docs
      // appear as they're inserted, then keep polling briefly for cover images.
      await api.post("/events/generate", body);
      const baselineIds = new Set(events.map((e) => e.id));
      let ticks = 0;
      let sawNew = false;
      const id = setInterval(async () => {
        ticks += 1;
        try {
          await load();
        } catch { /* ignore */ }
        if (!sawNew) {
          try {
            const res = await api.get("/events", {
              params: scope === "personal" ? { scope: "personal" } : { scope: "group", group_id: scope },
            });
            const fresh = (res.data || []).some((e) => !baselineIds.has(e.id));
            if (fresh) {
              sawNew = true;
              toast.success(t("eventsGenerated"));
              setGenerating(false);
            }
          } catch { /* ignore */ }
        }
        // Poll for ~90s to cover LLM + all cover images
        if (ticks >= 30) {
          clearInterval(id);
          setGenerating(false);
        }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
      setGenerating(false);
    }
  };

  const pickCategory = async (cat) => {
    setCategory(cat);
    // If we have no events for this scope+category combo, generate immediately
    const exists = events.some((e) => {
      const scopeMatch = scope === "personal" ? e.scope === "personal" : (e.scope === "group" && e.group_id === scope);
      const catMatch = cat ? e.category === cat : true;
      return scopeMatch && catMatch;
    });
    const key = `${scope}::${cat || "all"}`;
    if (!exists && !autoTried.current.has(key)) {
      autoTried.current.add(key);
      generate(cat);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/events/${id}`);
      setEvents((prev) => prev.filter((x) => x.id !== id));
    } catch { /* ignore */ }
  };

  const [regenIds, setRegenIds] = useState(new Set());
  const [scheduleFor, setScheduleFor] = useState(null); // event object
  const [scheduleDate, setScheduleDate] = useState(new Date());
  const [scheduleTime, setScheduleTime] = useState("18:00");
  const [scheduling, setScheduling] = useState(false);

  const openSchedule = (ev) => {
    setScheduleFor(ev);
    setScheduleDate(new Date());
    setScheduleTime("18:00");
  };

  const confirmSchedule = async () => {
    if (!scheduleFor) return;
    setScheduling(true);
    try {
      const [h, m] = scheduleTime.split(":").map((x) => parseInt(x, 10) || 0);
      const dt = new Date(scheduleDate);
      dt.setHours(h, m, 0, 0);
      await api.post("/scheduled-events", {
        title: scheduleFor.title,
        description: scheduleFor.description,
        category: scheduleFor.category,
        scheduled_at: dt.toISOString(),
        source_event_id: scheduleFor.id,
      });
      toast.success(t("addedToCalendar"));
      setScheduleFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
    } finally { setScheduling(false); }
  };

  const regenerateImage = async (id, e) => {
    e.stopPropagation();
    setRegenIds((s) => new Set(s).add(id));
    try {
      const res = await api.post(`/events/${id}/regenerate-image`);
      setEvents((prev) => prev.map((x) => (x.id === id ? { ...x, image_b64: res.data.image_b64 } : x)));
      toast.success(t("coverRegenerated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("networkErr"));
    } finally {
      setRegenIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  };

  const toggleSave = async (id, e) => {
    e.stopPropagation();
    // Optimistic flip
    setEvents((prev) => prev.map((x) => (x.id === id ? { ...x, saved: !x.saved } : x)));
    try {
      const res = await api.post(`/events/${id}/save`);
      setEvents((prev) => prev.map((x) => (x.id === id ? { ...x, saved: res.data.saved } : x)));
    } catch (err) {
      // Revert on failure
      setEvents((prev) => prev.map((x) => (x.id === id ? { ...x, saved: !x.saved } : x)));
      toast.error(err?.response?.data?.detail || t("networkErr"));
    }
  };

  const scopeLabel = scope === "personal"
    ? t("forYou")
    : (groups.find((g) => g.id === scope)?.name || t("forGroup"));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="events-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] font-bold">{t("events")}</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-semibold tracking-tight">{t("aiCuratedEvents")}</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{t("eventsSubtitle")}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-full sm:w-[200px] rounded-full h-11 bg-white border-[hsl(var(--border))]" data-testid="events-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="personal" data-testid="scope-personal">{t("forYou")}</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id} data-testid={`scope-${g.id}`}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {generating && (
            <span
              data-testid="events-generating-inline"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--elevated))] border border-[hsl(var(--border))]"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[hsl(var(--primary))]" />
              {t("generating")}
            </span>
          )}
        </div>
      </div>

      {/* Category chip row */}
      <div className="mb-6 -mx-1 overflow-x-auto" data-testid="category-chips">
        <div className="flex items-center gap-2 px-1 pb-2 min-w-max">
          <ChipWithShuffle
            label={<><span className="me-1.5">✨</span>{t("allCategories")}</>}
            active={!category}
            onSelect={() => pickCategory(null)}
            onShuffle={() => { setSource("all"); generate(null); }}
            shuffleDisabled={generating}
            testid="cat-chip-all"
          />
          {ACTIVITIES_PRIMARY.map((c) => (
            <ChipWithShuffle
              key={c}
              label={<><span className="me-1.5">{CAT_EMOJI[c] || "•"}</span>{c}</>}
              active={category === c}
              onSelect={() => pickCategory(c)}
              onShuffle={() => { setSource("all"); generate(c); }}
              shuffleDisabled={generating}
              testid={`cat-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
          ))}
        </div>
      </div>

      {/* Source filter — Saved is always available; Curated only when Dubai curated events exist */}
      <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="source-chips">
        <span className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))] font-bold me-1">
          {t("source")}
        </span>
        {[
          { key: "all", label: t("sourceAll"), show: true },
          { key: "curated", label: t("sourceCurated"), show: events.some((e) => e.curated) },
          { key: "ai", label: t("sourceAI"), show: events.some((e) => e.curated) },
          { key: "saved", label: t("sourceSaved"), show: true },
        ].filter((s) => s.show).map((s) => {
          const active = source === s.key;
          const savedCount = s.key === "saved" ? events.filter((e) => e.saved).length : 0;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setSource(s.key)}
              data-testid={`source-chip-${s.key}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all inline-flex items-center gap-1.5 ${
                active
                  ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                  : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"
              }`}
            >
              {s.key === "curated" && <span>✓</span>}
              {s.key === "saved" && <Heart className={`h-3 w-3 ${active ? "fill-current" : ""}`} />}
              {s.label}
              {s.key === "saved" && savedCount > 0 && (
                <span
                  data-testid="saved-count-badge"
                  className={`ms-0.5 text-[10px] font-bold px-1.5 rounded-full ${
                    active ? "bg-white/25" : "bg-[hsl(var(--elevated))]"
                  }`}
                >
                  {savedCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
      ) : generating && filtered.length === 0 ? (
        <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-12 text-center" data-testid="events-auto-generating">
          <div className="relative h-16 w-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-2xl bg-[hsl(var(--elevated))] flex items-center justify-center">
              <Sparkles className="h-7 w-7 text-[hsl(var(--primary))]" />
            </div>
            <div className="absolute inset-0 rounded-2xl border-2 border-[hsl(var(--primary))] border-t-transparent animate-spin" />
          </div>
          <div className="font-heading text-lg font-semibold">{t("generating")}</div>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] max-w-sm mx-auto">
            {t("eventsSubtitle")}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[hsl(var(--border))] rounded-3xl p-12 text-center" data-testid="events-empty">
          <div className="h-14 w-14 mx-auto rounded-2xl bg-[hsl(var(--elevated))] flex items-center justify-center mb-3">
            {source === "saved"
              ? <Heart className="h-7 w-7 text-[hsl(var(--destructive))]" />
              : <Sparkles className="h-7 w-7 text-[hsl(var(--primary))]" />}
          </div>
          <div className="font-heading text-lg font-semibold">
            {source === "saved" ? t("noSavedYet") : t("noEvents")}
          </div>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] max-w-sm mx-auto">
            {source === "saved" ? t("noSavedHint") : t("noEventsHint")}
          </p>
          {source === "saved" && (
            <button
              type="button"
              onClick={() => setSource("all")}
              data-testid="saved-empty-browse"
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white"
            >
              <Sparkles className="h-4 w-4" /> {t("browseIdeas")}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((ev, idx) => {
            const fallback = CAT_IMG[ev.category] || CAT_IMG.Other;
            const img = ev.image_b64 ? `data:image/png;base64,${ev.image_b64}` : fallback;
            return (
              <div
                key={ev.id}
                className="bg-white border border-[hsl(var(--border))] rounded-3xl overflow-hidden hover:-translate-y-1 transition-all fade-up shadow-[0_4px_24px_rgba(31,30,27,0.05)]"
                data-testid={`event-card-${idx}`}
                style={{ animationDelay: `${idx * 40}ms` }}
              >
                <div className="relative h-48 overflow-hidden bg-[hsl(var(--elevated))]">
                  <img
                    src={img}
                    alt={ev.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = fallback; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  <span className="absolute top-3 start-3 text-[10px] uppercase tracking-wider font-bold bg-white px-2.5 py-1 rounded-full text-[hsl(var(--secondary))]">
                    {ev.category}
                  </span>
                  <button
                    onClick={(e) => remove(ev.id, e)}
                    data-testid={`event-delete-${idx}`}
                    className="absolute top-3 end-3 h-8 w-8 rounded-full bg-white/95 flex items-center justify-center hover:bg-white"
                    aria-label="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" />
                  </button>
                  <button
                    onClick={(e) => regenerateImage(ev.id, e)}
                    disabled={regenIds.has(ev.id)}
                    data-testid={`event-regen-img-${idx}`}
                    aria-label="Regenerate cover"
                    className="absolute top-3 end-12 h-8 w-8 rounded-full bg-white/95 flex items-center justify-center hover:bg-white disabled:opacity-60"
                  >
                    {regenIds.has(ev.id)
                      ? <Loader2 className="h-3.5 w-3.5 text-[hsl(var(--primary))] animate-spin" />
                      : <RefreshCw className="h-3.5 w-3.5 text-[hsl(var(--foreground))]" />}
                  </button>
                  <button
                    onClick={(e) => toggleSave(ev.id, e)}
                    data-testid={`event-save-${idx}`}
                    aria-label={ev.saved ? t("unsave") : t("save")}
                    title={ev.saved ? t("unsave") : t("save")}
                    className="absolute top-3 end-[5.25rem] h-8 w-8 rounded-full bg-white/95 flex items-center justify-center hover:bg-white transition-transform hover:-translate-y-0.5"
                  >
                    <Heart
                      className={`h-3.5 w-3.5 transition-colors ${
                        ev.saved
                          ? "text-[hsl(var(--destructive))] fill-[hsl(var(--destructive))]"
                          : "text-[hsl(var(--foreground))]"
                      }`}
                    />
                  </button>
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <div className="text-[10px] uppercase tracking-wider font-semibold opacity-90">
                      {ev.scope === "group" ? `${t("forGroup")} · ${ev.group_name || ""}` : t("forYou")}
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-start gap-2">
                    <h3 className="font-heading text-lg font-semibold leading-tight flex-1">{ev.title}</h3>
                    {ev.curated && (
                      <span
                        data-testid={`curated-badge-${ev.id}`}
                        title="Curated by SocialHub"
                        className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-[10px] font-bold uppercase tracking-wider border border-[hsl(var(--primary))]/30"
                      >
                        <BadgeCheck className="h-3 w-3" />
                        {t("curated")}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{ev.description}</p>

                  {/* Concrete details */}
                  <div className="mt-3 space-y-1.5 text-xs">
                    {ev.location && (
                      <div className="flex items-start gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
                        <span className="text-[hsl(var(--foreground))]">{ev.location}</span>
                      </div>
                    )}
                    {ev.google_rating && (
                      <div className="flex items-start gap-1.5">
                        <Star className="h-3.5 w-3.5 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
                        <span className="text-[hsl(var(--foreground))]">{ev.google_rating}</span>
                      </div>
                    )}
                    {(ev.price_for_two || ev.price_for_group) && (
                      <div className="flex items-start gap-1.5">
                        <Wallet className="h-3.5 w-3.5 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
                        <span className="text-[hsl(var(--foreground))]">
                          {ev.price_for_two}
                          {ev.price_for_group && (
                            <span className="text-[hsl(var(--muted-foreground))]"> · {ev.price_for_group}</span>
                          )}
                        </span>
                      </div>
                    )}
                    {ev.best_day && (
                      <div className="flex items-start gap-1.5">
                        <CalendarCheck className="h-3.5 w-3.5 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
                        <span className="text-[hsl(var(--foreground))]">{ev.best_day}</span>
                      </div>
                    )}
                    {ev.promotion && (
                      <div className="flex items-start gap-1.5">
                        <Tag className="h-3.5 w-3.5 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
                        <span className="text-[hsl(var(--foreground))]">{ev.promotion}</span>
                      </div>
                    )}
                  </div>

                  {ev.why_match && (
                    <div className="mt-3 p-3 rounded-2xl bg-[hsl(var(--elevated))] border border-[hsl(var(--border))]">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--secondary))]">{t("why")}</div>
                      <div className="mt-0.5 text-sm">{ev.why_match}</div>
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                      <CalendarIcon className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                      {new Date(ev.created_at).toLocaleDateString()}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => openSchedule(ev)}
                      data-testid={`event-schedule-${idx}`}
                      className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white h-9 px-4 gap-1.5"
                    >
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {t("addToCalendar")}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule dialog */}
      <Dialog open={!!scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-[hsl(var(--primary))]" />
              {t("addToCalendar")}
            </DialogTitle>
            <DialogDescription>{scheduleFor?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-2xl border border-[hsl(var(--border))] p-2">
              <Calendar
                mode="single"
                selected={scheduleDate}
                onSelect={(d) => d && setScheduleDate(d)}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                className="w-full"
              />
            </div>
            <div>
              <Label className="text-sm">{t("time")}</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Clock className="h-4 w-4 text-[hsl(var(--primary))]" />
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="rounded-xl h-10 flex-1"
                  data-testid="schedule-time-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={confirmSchedule}
              disabled={scheduling}
              data-testid="schedule-confirm-btn"
              className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white gap-1.5"
            >
              {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {t("confirmSchedule")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ChipWithShuffle({ label, active, onSelect, onShuffle, shuffleDisabled, testid }) {
  return (
    <div className={`shrink-0 inline-flex items-stretch rounded-full border transition-all ${
      active
        ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))] shadow-[0_4px_14px_rgba(22,200,90,0.3)]"
        : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"
    }`}>
      <button
        type="button"
        onClick={onSelect}
        data-testid={testid}
        className="px-4 py-2 text-sm font-medium rounded-full focus:outline-none"
      >
        {label}
      </button>
      {active && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onShuffle(); }}
          disabled={shuffleDisabled}
          data-testid={`${testid}-shuffle`}
          aria-label="Shuffle ideas"
          title="Shuffle new ideas"
          className="pe-3.5 -ms-1.5 inline-flex items-center justify-center text-white/85 hover:text-white disabled:opacity-40 transition-transform hover:rotate-180 duration-300"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
